<template>
  <nav
    class="student-sidebar"
    :class="{ 'student-sidebar--compact': compact }"
    aria-label="Навігація кабінету учня"
  >
    <section class="student-sidebar__section">
      <header v-if="!compact" class="student-sidebar__heading">
        <div>
          <h2>Мої предмети</h2>
        </div>
      </header>

      <div class="student-sidebar__courses">
        <button
          v-for="(item, index) in courses"
          :key="item.id"
          type="button"
          class="student-sidebar__course"
          :class="{
            'student-sidebar__course--active': isCourseActive(item.id),
          }"
          :title="item.name"
          @click="getLesson(item.id)"
        >
          <span
            class="student-sidebar__course-icon"
            :style="courseIconStyle(index)"
            aria-hidden="true"
          >
            <v-icon
              v-if="item.icon"
              :color="courseIconStyle(index).color"
              size="18"
            >
              {{ item.icon }}
            </v-icon>
            <template v-else>{{ courseInitial(item.name) }}</template>
          </span>
          <span v-if="!compact" class="student-sidebar__course-name">{{
            item.name
          }}</span>
          <span
            v-if="item.hasMessages"
            class="student-sidebar__notification"
            aria-label="Є нові повідомлення"
            title="Є нові повідомлення"
          >
            <v-icon size="20">mdi-bell-outline</v-icon>
            <span
              class="student-sidebar__notification-dot"
              aria-hidden="true"
            ></span>
          </span>
          <v-icon
            v-if="!compact && isCourseActive(item.id)"
            class="student-sidebar__chevron"
            size="18"
            >mdi-chevron-right</v-icon
          >
        </button>
      </div>
    </section>

    <section
      class="student-sidebar__section student-sidebar__section--navigation"
    >
      <button
        v-if="smallScreen"
        type="button"
        class="student-sidebar__link"
        :class="{
          'student-sidebar__link--active': currentRoute === states.CALENDAR,
        }"
        @click="navigate(states.CALENDAR)"
      >
        <span class="student-sidebar__link-icon"
          ><v-icon size="21">mdi-calendar-blank-outline</v-icon></span
        >
        <span v-if="!compact">Календар</span>
      </button>
      <button
        v-if="smallScreen"
        type="button"
        class="student-sidebar__link"
        :class="{
          'student-sidebar__link--active': currentRoute === states.LIBRARY,
        }"
        @click="navigate(states.LIBRARY)"
      >
        <span class="student-sidebar__link-icon"
          ><v-icon size="21">mdi-bookshelf</v-icon></span
        >
        <span v-if="!compact">Бібліотека</span>
      </button>
      <button
        type="button"
        class="student-sidebar__link"
        :class="{
          'student-sidebar__link--active': currentRoute === states.REPORT_CARD,
        }"
        @click="navigate(states.REPORT_CARD)"
      >
        <span class="student-sidebar__link-icon"
          ><v-icon size="21">mdi-chart-box-outline</v-icon></span
        >
        <span v-if="!compact">Табель</span>
      </button>
    </section>
  </nav>
</template>

<script>
import statesEnum from '@/enums/statesEnum';

const COURSE_COLORS = [
  { backgroundColor: '#eaf6f1', color: '#24795d' },
  { backgroundColor: '#eef4fc', color: '#2d68bd' },
  { backgroundColor: '#fff3e8', color: '#b3651f' },
  { backgroundColor: '#f4effa', color: '#7657a5' },
  { backgroundColor: '#fff7d9', color: '#9a7200' },
];

export default {
  name: 'CourseItem',
  props: {
    smallScreen: Boolean,
    compact: Boolean,
  },
  data: () => ({
    states: statesEnum,
  }),
  computed: {
    courses() {
      return [...this.$store.getters['CourseItemsStore/getCourses']].sort(
        (first, second) => String(first.name || '').localeCompare(
          String(second.name || ''),
          'uk',
          { sensitivity: 'base' }
        )
      );
    },
    currentCourseId() {
      return this.$store.getters['LessonsStore/getCurrentCourseId'];
    },
    currentRoute() {
      return this.$route.name;
    },
  },
  created() {
    this.getCourses();
  },
  methods: {
    async getCourses() {
      await this.$store.dispatch('CourseItemsStore/getItems', {
        token: this.token,
      });
      this.openFirstCourseIfNeeded();
    },
    openFirstCourseIfNeeded() {
      if (
        this.$route.name !== statesEnum.CLASS ||
        this.$route.query.courseId ||
        !this.courses.length
      )
        return;

      this.$router.replace({
        name: statesEnum.CLASS,
        query: { ...this.$route.query, courseId: this.courses[0].id },
      });
    },
    getLesson(courseId) {
      this.$emit('courseItemsEvent', { type: 'getLesson', data: { courseId } });
      this.navigate(statesEnum.CLASS, { courseId });
    },
    navigate(name, query) {
      if (
        this.$route.name === name &&
        (!query ||
          String(this.$route.query.courseId) === String(query.courseId))
      )
        return;
      this.$router.push({ name, query });
    },
    courseInitial(name) {
      return String(name || '?')
        .trim()
        .charAt(0)
        .toUpperCase();
    },
    courseIconStyle(index) {
      return COURSE_COLORS[index % COURSE_COLORS.length];
    },
    isCourseActive(courseId) {
      return (
        this.currentRoute === statesEnum.CLASS &&
        courseId === this.currentCourseId
      );
    },
  },
};
</script>

<style lang="less" scoped>
.student-sidebar {
  background: #f5f7f6;
  color: #172033;
  min-height: 100%;
  padding: 22px 14px 18px;
}
.student-sidebar__section + .student-sidebar__section {
  margin-top: 24px;
}
.student-sidebar__heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0 8px 13px;
}
.student-sidebar__heading h2 {
  color: #172033;
  font-size: 18px;
  margin: 0;
}
.student-sidebar__courses {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.student-sidebar__course,
.student-sidebar__link {
  align-items: center;
  background: transparent;
  border: 0;
  color: #465160;
  cursor: pointer;
  display: flex;
  min-width: 0;
  text-align: left;
  width: 100%;
}
.student-sidebar__course {
  background: #fff;
  border: 1px solid #e8ecea;
  border-radius: 11px;
  gap: 10px;
  min-height: 47px;
  padding: 6px 9px;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s,
    color 0.15s;
}
.student-sidebar__course:hover {
  background: #f2f5f4;
  color: #172033;
}
.student-sidebar__course--active {
  background: var(--theme-surface-decorative) !important;
  border-color: var(--theme-accent-decorative);
  box-shadow: inset 3px 0 var(--theme-accent-decorative),
    0 3px 10px rgba(31, 41, 55, 0.05);
  color: var(--theme-banner-text) !important;
}
.student-sidebar__course-icon {
  align-items: center;
  border-radius: 9px;
  display: flex;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 900;
  height: 32px;
  justify-content: center;
  width: 32px;
}
.student-sidebar__course-name {
  display: -webkit-box;
  flex: 1 1 auto;
  font-size: 14px;
  font-weight: 650;
  line-height: 1.25;
  min-width: 0;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.student-sidebar__notification {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  height: 28px;
  justify-content: center;
  position: relative;
  width: 28px;
}
.student-sidebar__notification::v-deep .v-icon {
  color: var(--theme-header-background) !important;
}
.student-sidebar__notification-dot {
  background: var(--semantic-error);
  border: 2px solid var(--surface-primary);
  border-radius: 50%;
  height: 9px;
  position: absolute;
  right: 1px;
  top: 1px;
  width: 9px;
}
.student-sidebar__chevron {
  color: var(--theme-accent-decorative) !important;
  flex: 0 0 auto;
}
.student-sidebar__section--navigation {
  border-top: 1px solid #e5e9ec;
  padding-top: 18px;
}
.student-sidebar__link {
  background: #fff;
  border: 1px solid #e8ecea;
  border-radius: 11px;
  font-size: 14px;
  font-weight: 700;
  gap: 11px;
  margin-bottom: 5px;
  min-height: 46px;
  padding: 6px 9px;
}
.student-sidebar__link:hover {
  background: #f2f5f4;
  color: #172033;
}
.student-sidebar__link--active {
  background: #eaf6f1;
  color: #176d50;
}
.student-sidebar__link-icon {
  align-items: center;
  background: #f1f4f5;
  border-radius: 9px;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;
}
.student-sidebar__link--active .student-sidebar__link-icon {
  background: #dcefe7;
}
.student-sidebar__link::v-deep .v-icon {
  color: inherit;
}
.student-sidebar--compact {
  padding-left: 8px;
  padding-right: 8px;
}
.student-sidebar--compact .student-sidebar__courses {
  gap: 7px;
}
.student-sidebar--compact .student-sidebar__course {
  justify-content: center;
  min-height: 48px;
  padding: 7px;
  position: relative;
}
.student-sidebar--compact .student-sidebar__notification {
  position: absolute;
  right: 0;
  top: 0;
}
.student-sidebar--compact .student-sidebar__section--navigation {
  padding-top: 14px;
}
.student-sidebar--compact .student-sidebar__link {
  justify-content: center;
  padding: 6px;
}
</style>
